import { useEffect, useRef } from 'react'
import { animate, stagger } from 'animejs'
import '../styles/signal-finale.css'

export function SignalFinale({ motionEnabled }: { motionEnabled: boolean }) {
  const sectionRef = useRef<HTMLElement>(null)

  useEffect(() => {
    const section = sectionRef.current
    if (!section || !motionEnabled) return
    let revealed = false
    let reveal: ReturnType<typeof animate> | undefined
    const observer = new IntersectionObserver(([entry]) => {
      section.dataset.inView = String(entry.isIntersecting && !document.hidden)
      if (!entry.isIntersecting || revealed) return
      revealed = true
      reveal = animate(section.querySelectorAll('[data-contact-line]'), {
        y: ['105%', '0%'],
        opacity: [0, 1],
        delay: stagger(110),
        duration: 1_100,
        ease: 'outExpo',
      })
    }, { threshold: 0.16 })
    const onVisibility = () => {
      const bounds = section.getBoundingClientRect()
      section.dataset.inView = String(!document.hidden && bounds.bottom > 0 && bounds.top < innerHeight)
    }
    observer.observe(section)
    document.addEventListener('visibilitychange', onVisibility)
    return () => {
      observer.disconnect()
      document.removeEventListener('visibilitychange', onVisibility)
      reveal?.revert()
      section.dataset.inView = 'false'
    }
  }, [motionEnabled])

  return (
    <section
      ref={sectionRef}
      className="signal-finale"
      id="finale"
      aria-labelledby="finale-title"
      data-motion={motionEnabled ? 'on' : 'off'}
    >
      <div className="signal-finale__masthead">
        <span>END OF TRANSMISSION</span>
        <span>THE SOURCE IS STILL OUT THERE</span>
      </div>

      <div className="signal-finale__field" aria-hidden="true">
        <div className="signal-finale__aura" />
        <svg className="signal-finale__orbit" viewBox="0 0 700 700" fill="none">
          <circle cx="350" cy="350" r="294" strokeDasharray="1 9" />
          <circle cx="350" cy="350" r="248" />
          <circle cx="350" cy="350" r="182" strokeDasharray="100 470" />
          <path d="M350 8v88m0 508v88M8 350h88m508 0h88" />
          <path d="m124 124 14 14m424 424 14 14m0-452-14 14M138 562l-14 14" />
        </svg>
        <span className="signal-finale__echo signal-finale__echo--one" />
        <span className="signal-finale__echo signal-finale__echo--two" />
        <span className="signal-finale__echo signal-finale__echo--three" />
        <svg className="signal-finale__glyph" viewBox="0 0 600 600">
          <path d="M300 30c16 208 20 240 290 270-270 30-274 62-290 270C284 362 280 330 10 300c270-30 274-62 290-270Z" />
        </svg>
        <span className="signal-finale__coordinate">NVA / ∞<br />CONTACT REMAINS OPEN</span>
      </div>

      <div className="signal-finale__content">
        <p className="signal-label">THREE FREQUENCIES RECOVERED</p>
        <h2 id="finale-title">
          <span><span data-contact-line>Keep</span></span>
          <span><span data-contact-line>looking</span></span>
          <span><span data-contact-line>up.</span></span>
        </h2>
        <div className="signal-finale__closing">
          <p>We found a signal in the dark.<br />Imagine what else is out there.</p>
          <a className="signal-finale__return" href="#top">
            <span>Return to first contact</span>
            <span className="signal-finale__return-icon" aria-hidden="true">↗</span>
          </a>
        </div>
      </div>
      <div className="signal-finale__baseline" aria-hidden="true">
        <span>STOLEN FROM THE SKIES.</span><span>SEALED ON EARTH.</span>
      </div>
    </section>
  )
}
